import { create } from 'zustand';
import { mockProjects, Project } from '../data/mockProjects';
import { mockIpAssets, IpAsset } from '../data/mockIpAssets';
import { mockStakeholders, Stakeholder } from '../data/mockStakeholders';
import { mockContracts, Contract } from '../data/mockContracts';
import { mockUsers, mockRoles, User, Role } from '../data/mockUsers';

interface AppState {
  // Projects
  projects: Project[];
  addProject: (project: Project) => void;
  updateProject: (id: string, project: Partial<Project>) => void;
  
  // IP Assets
  ipAssets: IpAsset[];
  addIpAsset: (ipAsset: IpAsset) => void;
  
  // Stakeholders
  stakeholders: Stakeholder[];
  addStakeholder: (stakeholder: Stakeholder) => void;
  
  // Contracts
  contracts: Contract[];
  addContract: (contract: Contract) => void;
  updateContract: (id: string, contract: Partial<Contract>) => void;
  
  // Users and Roles
  users: User[];
  roles: Role[];
  addUser: (user: User) => void;
  updateUser: (id: string, user: Partial<User>) => void;
  deleteUser: (id: string) => void;
  
  // Current User
  user: { name: string; email: string } | null;
  setUser: (user: { name: string; email: string } | null) => void;
}

export const useAppStore = create<AppState>((set) => ({
  // Initial data
  projects: mockProjects,
  ipAssets: mockIpAssets,
  stakeholders: mockStakeholders,
  contracts: mockContracts,
  users: mockUsers,
  roles: mockRoles,
  user: null,
  
  // Actions
  addProject: (project) => 
    set((state) => ({ projects: [...state.projects, project] })),
  
  updateProject: (id, updatedProject) =>
    set((state) => ({
      projects: state.projects.map((p) =>
        p.id === id ? { ...p, ...updatedProject } : p
      ),
    })),
  
  addIpAsset: (ipAsset) =>
    set((state) => ({ ipAssets: [...state.ipAssets, ipAsset] })),
  
  addStakeholder: (stakeholder) =>
    set((state) => ({ stakeholders: [...state.stakeholders, stakeholder] })),
  
  addContract: (contract) =>
    set((state) => ({ contracts: [...state.contracts, contract] })),
  
  updateContract: (id, updatedContract) =>
    set((state) => ({
      contracts: state.contracts.map((c) =>
        c.id === id ? { ...c, ...updatedContract } : c
      ),
    })),
  
  addUser: (user) =>
    set((state) => ({ users: [...state.users, user] })),
  
  updateUser: (id, updatedUser) =>
    set((state) => ({
      users: state.users.map((u) =>
        u.id === id ? { ...u, ...updatedUser } : u
      ),
    })),
  
  deleteUser: (id) =>
    set((state) => ({
      users: state.users.filter((u) => u.id !== id),
    })),
  
  setUser: (user) => set({ user }),
}));
